/**
 * Vigia o SILÊNCIO do Web Worker que carrega o socket do STOMP (R2CLOUD-1165).
 *
 * O defeito medido em 31/07/2026 contra o DEV: em 6 de 16 logins limpos o `new Worker(…,
 * {type:'module'})` do `StompService.initWorker()` é criado, o `postMessage(OPEN)` sai 3-4 ms
 * depois — e o worker NÃO EXECUTA. Nenhuma mensagem de volta em 50 s, nenhum evento `error`, e o
 * handshake `wss://…/stomp/web` nunca sai do browser (confirmado pelo log de acesso do
 * ingress-nginx: zero `101` nas execuções em falha). No caminho saudável a primeira resposta do
 * worker chega em **1,26 a 1,89 s**.
 *
 * ⚠️ **O worker não está morto — está lento.** Numa das execuções ele acordou ~71 s depois e
 * ENTÃO abriu o socket, o que prova que a mensagem ficou na fila em vez de se perder. É por isso
 * que o watchdog **descarta** o worker antigo (o teardown do `fromWorker` chama `terminate()`)
 * antes de criar outro: sem isso, o worker atrasado acordaria e abriria uma SEGUNDA sessão STOMP
 * para o mesmo usuário.
 *
 * O que ele mede é o silêncio do worker, e não "o STOMP conectou". A distinção não é preciosismo:
 * assim que o socket abre, o worker devolve um `StompEventType.OPEN` — logo, se ele já falou, o
 * socket já pode existir, e derrubá-lo por causa de um servidor lento em responder o frame
 * `CONNECTED` é justamente o que produziria duas conexões. Qualquer mensagem vinda do worker
 * desarma o watchdog.
 *
 * Sem dependência de Angular nem de rxjs, de propósito — testado em
 * test/stomp-worker-watchdog.test.ts com relógio falso.
 */

/**
 * Prazo de silêncio tolerado. ~5x o pior tempo saudável medido (1,89 s), com folga larga para
 * máquina lenta: no caminho saudável o watchdog é desarmado muito antes de disparar.
 */
export const WORKER_SILENCE_TIMEOUT_MS = 10000;

/** Só existe para o teste poder injetar um relógio falso. */
export interface WatchdogTimers {
    set(handler: () => void, delayMs: number): number;

    clear(id: number): void;
}

export const browserTimers: WatchdogTimers = {
    set: (handler: () => void, delayMs: number): number => window.setTimeout(handler, delayMs),
    clear: (id: number): void => window.clearTimeout(id),
};

export class StompWorkerWatchdog {
    private timerId: number | null = null;

    constructor(
        private readonly timeoutMs: number = WORKER_SILENCE_TIMEOUT_MS,
        private readonly timers: WatchdogTimers = browserTimers,
    ) {
    }

    get armed(): boolean {
        return this.timerId !== null;
    }

    /**
     * Arma o watchdog para a tentativa de conexão corrente. Rearmar cancela o timer anterior — sem
     * isso, uma segunda tentativa deixaria o timer da primeira vivo e o worker novo seria descartado
     * por causa do silêncio do worker velho.
     */
    arm(onSilence: () => void): void {
        this.disarm();
        this.timerId = this.timers.set(() => {
            this.timerId = null;
            onSilence();
        }, this.timeoutMs);
    }

    disarm(): void {
        if (this.timerId !== null) {
            this.timers.clear(this.timerId);
            this.timerId = null;
        }
    }
}
